import React, { useState, useMemo } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  Theme,
  TableSortLabel,
  Box,
  Chip,
  Tooltip,
  Link,
} from "@mui/material";
import { observer } from "mobx-react";
import { OpsSyncAnalyticsData } from "../../../../../models/Dashboard/OpsSyncAnalyticsData";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTablePagenation from "../../../../../views/components/common/pages/AppTablePagenation";
import { SortType } from "../../../../../models/SortType";
import {
  formatDate,
  formatDateWithoutTime,
} from "../../../../../views/components/common/functions/formatDate";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";

interface OpsSyncAnalyticsTableProps {
  maxHeight?: number;
  data: OpsSyncAnalyticsData[];
}

const OpsSyncAnalyticsTable: React.FC<OpsSyncAnalyticsTableProps> = ({
  maxHeight = 600,
  data,
}) => {
  const theme = useTheme<Theme>();

  // Local state for pagination and sorting
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<string>("order_id");
  const [sortType, setSortType] = useState<SortType>(SortType.DESC);
  const recordsPerPage = 50;

  const handleRowsChange = (value: number) => {
    setCurrentPage(1);
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => {
    setCurrentPage(page);
  };

  const handleSortChange = (column: string, sortType: SortType) => {
    setSortBy(column);
    setSortType(sortType);
    setCurrentPage(1); // Reset to first page when sorting
  };

  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  const formatDateValue = (date: Date | null) => {
    if (!date) return "-";
    return formatDateWithoutTime(date);
  };

  const getStatusChip = (
    value: boolean | null,
    trueLabel: string,
    falseLabel: string,
  ) => {
    if (value === null)
      return <Chip label="Pending" size="small" variant="outlined" />;
    return (
      <Chip
        label={value ? trueLabel : falseLabel}
        size="small"
        color={value ? "success" : "error"}
        variant="outlined"
      />
    );
  };

  const getQuantityDisplay = (value: number | null) => {
    if (value === null || value === 0) return "-";
    return value.toString();
  };

  const getVendorsDisplay = (vendors: string | null) => {
    if (!vendors) return "-";
    const vendorList = vendors.split(",");
    if (vendorList.length <= 2) return vendors;
    return (
      <Tooltip title={vendors}>
        <span>
          {vendorList.slice(0, 2).join(", ")} +{vendorList.length - 2}
        </span>
      </Tooltip>
    );
  };

  // Local sorting and pagination logic
  const sortedAndPaginatedData = useMemo(() => {
    // Sort the data
    const sortedData = [...data].sort((a, b) => {
      const aValue = (a as any)[sortBy];
      const bValue = (b as any)[sortBy];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Handle dates
      if (aValue instanceof Date && bValue instanceof Date) {
        return sortType === SortType.ASC
          ? aValue.getTime() - bValue.getTime()
          : bValue.getTime() - aValue.getTime();
      }

      // Handle numbers
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortType === SortType.ASC ? aValue - bValue : bValue - aValue;
      }

      // Handle strings
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      return sortType === SortType.ASC
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });

    // Paginate the sorted data
    const startIndex = (currentPage - 1) * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [data, sortBy, sortType, currentPage, recordsPerPage]);

  const totalPages = Math.ceil(data.length / recordsPerPage);
  const totalCount = data.length;

  const handleDownload = () => {
    // Define headers for CSV
    const headers = [
      "S.No",
      "Order ID",
      "Created Date",
      "Warehouse",
      "Order Type",
      "Customer",
      "Dispatch Date",
      "Expected Inward",
      "Raw Inward Completed",
      "Print Inward Completed",
      "Delivery Date",
      "Sales POC",
      "PM POC",
      "Shipments Count",
      "Invoice Status",
      "Product",
      "Ordered Qty",
      "Mockups Approved",
      "Last Mockup Date",
      "Last PR Date",
      "PR Vendors",
      "PR Qty",
      "PO IDs",
      "PO Qty",
      "RI Received",
      "RI QC Started",
      "RI Rejected",
      "Customization",
      "Printers",
      "PI Received",
      "PI QC Started",
      "PI Rejected",
    ];

    // Convert data to CSV rows
    const rows = data.map((row, index) => [
      index + 1,
      row.order_id,
      row.project_created_date
        ? formatDateWithoutTime(row.project_created_date)
        : "",
      row.warehouse_id || "",
      row.order_type || "",
      row.customer_name || "",
      row.dispatch_date ? formatDateWithoutTime(row.dispatch_date) : "",
      row.expected_inward_date
        ? formatDateWithoutTime(row.expected_inward_date)
        : "",
      row.raw_inward_completed ? "Completed" : "Not Completed",
      row.print_inward_completed ? "Completed" : "Not Completed",
      row.delivery_date ? formatDateWithoutTime(row.delivery_date) : "",
      row.sales_poc || "",
      row.pm_poc || "",
      row.shipments_count || "",
      Boolean(row.invoiced)
        ? row.invoice_amount! - row.total_amount! >= 0
          ? "Paid"
          : "Partial"
        : "Pending",
      `${row.product_id} ${row.product_name || "-"}`,
      row.ordered_quantity,
      row.all_mockups_approved ? "All" : "Partial",
      row.last_mockup_approved_date
        ? formatDateWithoutTime(row.last_mockup_approved_date)
        : "",
      row.last_pr_date ? formatDateWithoutTime(row.last_pr_date) : "",
      row.pr_vendors || "",
      row.pr_quantity || "",
      row.po_ids || "",
      row.po_quantity || "",
      row.ri_total_received_quantity || "",
      row.ri_qc_started ? "Started" : "Not Started",
      row.ri_total_rejected_quantity || "",
      row.customisation_required ? "Required" : "Not Required",
      row.printers || "",
      row.pi_total_received_quantity || "",
      row.pi_qc_started ? "Started" : "Not Started",
      row.pi_total_rejected_quantity || "",
    ]);

    // Escape CSV special characters
    const escapeCsv = (value: any) => {
      const cell = String(value ?? "").replace(/"/g, '""');
      return `"${cell}"`;
    };

    const csvContent = [
      headers.map(escapeCsv).join(","),
      ...rows.map((row) => row.map(escapeCsv).join(",")),
    ].join("\n");

    const now = dayjs().format("YYYY-MM-DD_HH:mm:ss");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Ops_Sync_Analytics_${now}.csv`);
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Paper
      sx={{
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          padding: "16px 20px",
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.customColors.lightGreyColor,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: theme.palette.customColors.blackBorder,
            fontSize: "1.1rem",
          }}
        >
          Operations Sync Analytics
        </Typography>

        <Box display="flex" justifyContent="flex-end">
          <button
            onClick={handleDownload}
            style={{
              padding: "10px 16px",
              backgroundColor: theme.palette.customColors.purple,
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontWeight: 600,
              fontSize: "0.875rem",
              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor =
                theme.palette.customColors.purple;
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow =
                "0 4px 12px rgba(0, 0, 0, 0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor =
                theme.palette.customColors.purple;
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 2px 6px rgba(0, 0, 0, 0.1)";
            }}
          >
            <FontAwesomeIcon icon={faDownload} fontSize="0.875rem" />
            Download Data
          </button>
        </Box>
      </Box>

      {data.length > 0 ? (
        <Box>
          <TableContainer sx={{ maxHeight: maxHeight }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      position: "sticky",
                      left: 0,
                      zIndex: 20,
                      boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                      backgroundColor:
                        theme.palette.customColors.lightGreyColor,
                      minWidth: "60px",
                    }}
                  >
                    S.No
                  </AppTableHeaderCell>

                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      position: "sticky",
                      left: 60,
                      zIndex: 20,
                      boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                      backgroundColor:
                        theme.palette.customColors.lightGreyColor,
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "order_id"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        handleSortChange(
                          "order_id",
                          sortBy === "order_id" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("order_id")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Order ID
                    </TableSortLabel>
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    <TableSortLabel
                      active={sortBy === "project_created_date"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        handleSortChange(
                          "project_created_date",
                          sortBy === "project_created_date" &&
                            sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("project_created_date")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Created Date
                    </TableSortLabel>
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    Warehouse
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    Order Type
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="250px">
                    Customer
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    <TableSortLabel
                      active={sortBy === "dispatch_date"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        handleSortChange(
                          "dispatch_date",
                          sortBy === "dispatch_date" &&
                            sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("dispatch_date")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Dispatch Date
                    </TableSortLabel>
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    <TableSortLabel
                      active={sortBy === "expected_inward_date"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        handleSortChange(
                          "expected_inward_date",
                          sortBy === "expected_inward_date" &&
                            sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("expected_inward_date")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Expected Inward
                    </TableSortLabel>
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    RI Status
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    PI Status
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    <TableSortLabel
                      active={sortBy === "delivery_date"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        handleSortChange(
                          "delivery_date",
                          sortBy === "delivery_date" &&
                            sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("delivery_date")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Delivery Date
                    </TableSortLabel>
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="120px">
                    Sales POC
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="120px">
                    PM POC
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="120px">
                    Shipments Count
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="120px">
                    Invoice Status
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="220px">
                    Product
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    Ordered Qty
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    Mockups Approved
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    Last Mockup Date
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    <TableSortLabel
                      active={sortBy === "last_pr_date"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        handleSortChange(
                          "last_pr_date",
                          sortBy === "last_pr_date" &&
                            sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("last_pr_date")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Last PR Date
                    </TableSortLabel>
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="150px">
                    PR Vendors
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    PR Qty
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    PO IDs
                  </AppTableHeaderCell>
                  <AppTableHeaderCell minWidth="100px">
                    PO Qty
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    RI Received
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    RI QC Started
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    RI Rejected
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    Customization
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="150px">
                    Printers
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    PI Received
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    PI QC Started
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    PI Rejected
                  </AppTableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedAndPaginatedData.map((row, index) => (
                  <TableRow
                    key={`${row.order_id}-${row.product_id}-${index}`}
                    hover
                    sx={{
                      "&:nth-of-type(odd)": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        textAlign: "center",
                        left: 0,
                        position: "sticky",
                        zIndex: 15,
                        boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                        backgroundColor: theme.palette.background.paper,
                        fontWeight: 600,
                        color: theme.palette.customColors.greyTextColor,
                        minWidth: "60px",
                      }}
                    >
                      {(currentPage - 1) * recordsPerPage + index + 1}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        textAlign: "center",
                        left: 60,
                        position: "sticky",
                        zIndex: 15,
                        boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                        backgroundColor: theme.palette.background.paper,
                        fontWeight: 600,
                        color: theme.palette.customColors.purple,
                      }}
                    >
                      {
                        <Link
                          href={`/projects/${row.order_id}?tab_index=1`}
                          target="_blank"
                        >
                          {row.order_id}
                        </Link>
                      }
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {formatDateValue(row.project_created_date)}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {row.warehouse_id || "-"}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      <Chip
                        label={row.order_type || "N/A"}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {row.customer_name || "-"}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {formatDateValue(row.dispatch_date)}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {formatDateValue(row.expected_inward_date)}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {getStatusChip(
                        row.raw_inward_completed,
                        "Completed",
                        "Not Completed",
                      )}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {getStatusChip(
                        row.print_inward_completed,
                        "Completed",
                        "Not Completed",
                      )}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {formatDateValue(row.delivery_date)}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {row.sales_poc || "-"}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {row.pm_poc || "-"}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {getQuantityDisplay(row.shipments_count)}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {Boolean(row.invoiced)
                        ? getStatusChip(
                            row.invoice_amount! - row.total_amount! >= 0,
                            "Paid",
                            "Partial",
                          )
                        : getStatusChip(false, "Pending", "Pending")}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {
                        <Link
                          href={`/products/${row.product_id}`}
                          target="_blank"
                        >
                          {`(${row.product_id}) ${row.product_name || "-"}`}
                        </Link>
                      }
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {getQuantityDisplay(row.ordered_quantity)}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {getStatusChip(
                        row.all_mockups_approved,
                        "All",
                        "Partial",
                      )}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {formatDateValue(row.last_mockup_approved_date)}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {formatDateValue(row.last_pr_date)}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {getVendorsDisplay(row.pr_vendors)}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      <Link
                        href={`/purchase/requests/listing?page_no=1&search_text=${row.order_id}&limit=30&sort_by=created_date&sort_type=DESC&is_pending=false`}
                        target="_blank"
                      >
                        {getQuantityDisplay(row.pr_quantity)}
                      </Link>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {row.po_ids || "-"}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {getQuantityDisplay(row.po_quantity)}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      <Link
                        href={`/projects/${row.order_id}?tab_index=2`}
                        target="_blank"
                      >
                        {getQuantityDisplay(row.ri_total_received_quantity)}
                      </Link>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {getStatusChip(
                        row.ri_qc_started,
                        "Started",
                        "Not Started",
                      )}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {getQuantityDisplay(row.ri_total_rejected_quantity)}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {getStatusChip(
                        row.customisation_required,
                        "Required",
                        "Not Required",
                      )}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {row.printers || "-"}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {getQuantityDisplay(row.pi_total_received_quantity)}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {getStatusChip(
                        row.pi_qc_started,
                        "Started",
                        "Not Started",
                      )}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {getQuantityDisplay(row.pi_total_rejected_quantity)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <AppTablePagenation
            fontSize="12px"
            pageNo={currentPage}
            limit={recordsPerPage}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            handleRowsChange={handleRowsChange}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "200px",
            color: theme.palette.text.secondary,
          }}
        >
          <Typography variant="body1">No data available</Typography>
        </Box>
      )}
    </Paper>
  );
};

export default observer(OpsSyncAnalyticsTable);
